import moment from 'moment';
import merge from 'lodash-es/merge';

export const getPercentageColorClass = pct => {
  if (pct === null) return '';

  if (pct >= 1) return 'text-red';
  if (pct >= 0.9) return 'text-orange';
  if (pct >= 0.7) return 'text-orange';
  if (pct <= 0.3) return 'text-success';
  return '';
};

export const getPercentageColorBgClass = pct => {
  if (pct === null) return '';

  if (pct >= 1) return 'bg-red';
  if (pct >= 0.9) return 'bg-orange';
  if (pct >= 0.7) return 'bg-orange';
  if (pct <= 0.3) return 'bg-success';
  return '';
};

export const getFomattedPercentage = (pct, max = 999) =>
  `${Math.min(max, Math.round((pct || 0) * 100))}%`;

export const getUsedBudgetPercentage = (budget, actual) => (budget ? (actual || 0) / budget : null);

export const areHoursEqual = (a, b) => Math.abs(a - b) < 0.01;

export const randomActuals = budget => {
  const result = [];
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const v = Math.round(Math.random() * (budget * 0.13) * 100) / 100;
    sum += v;
    result[i] = sum;
  }
  return result;
};

export const budgetDataToMonthGraph = (data, budget, plannedDuration = 0) => {
  if (!data || (!budget && !plannedDuration)) return ['', '', '', ''];
  // The graph has a fixed size of 240 width and 100 height
  const topPadding = 5; // Extra space on top
  const height = 90; // Maximum height of graph
  const max = data.reduce((m, i) => (i > m ? i : m), -1);
  // Everything is drawn relative to the highest of the three, so a planned duration above the budget
  // (or no budget at all) still fits the box.
  const scale = Math.max(max, budget || 0, plannedDuration || 0);
  const y = value => (topPadding + height * (1 - value / scale)).toFixed(5);

  const actual = data.map((x, i) => `${10 + 20 * i},${y(x)}`).join(' ');
  const bottom = topPadding + height;
  const expected = budget ? `0,${bottom} 240,${y(budget)}` : '';
  const planned = plannedDuration ? `0,${bottom} 240,${y(plannedDuration)}` : '';

  // A flat line at the top of the box adds nothing, so only draw it when something rises above it.
  const budgetLine = budget && scale > budget ? `0,${y(budget)} 240,${y(budget)}` : '';
  return [actual, expected, budgetLine, planned];
};

export const makeCumulative = array => {
  if (!array) return null;
  return array.reduce((a, v) => {
    a.push((a[a.length - 1] || 0) + v);
    return a;
  }, []);
};

export const mapBudgetOverview = (e, allSelected) => ({
  ...e,
  budget: e.budget,
  amountPerMonth: makeCumulative(e.amountPerMonth),
  hoursPerMonth: makeCumulative(e.hoursPerMonth),
  selected: allSelected,
});

export const noActuals = ({ actualAmount, actualHours, ...e }) => ({ ...e });

// Clear id & budget values
export const clearBudget = ({ id, ...e }) => ({
  ...e,
  budgetAmount: null,
  budgetHours: null,
  isNew: true,
});

const getRegistrationPeriodFilter = year => {
  if (year === moment.utc().year()) return 'year';
  if (year === moment.utc().year() - 1) return 'prevYear';
  return 'all';
};

const getRegistrationOverviewNavParamsFromCalcSettings = settings => {
  if (!settings || Object.keys(settings).length === 0) return {};

  const result = { filters: {} };
  if (!settings.includeExForfait && !settings.includeInForfait) {
    result.filters.invoiceForfaitStatus = { items: [0] }; // Only NVT forfait status
  } else if (!settings.includeExForfait) {
    result.filters.invoiceForfaitStatus = { items: [0, 2] }; // Only NVT & included
  } else if (!settings.includeInForfait) {
    result.filters.invoiceForfaitStatus = { items: [0, 1] }; // Only NVT & not included
  }

  if (!settings.includeNotBillable) {
    result.filters.isBillable = { items: [true] };
  }
  if (settings.onlyNumberedInvoices) {
    result.filters.invoiceStatus = { items: [2] };
  }

  return result;
};

export const getRegistrationOverviewNavParams = (
  year,
  relationId,
  invoiceStreamId,
  calcSettings = null,
) =>
  merge(
    {
      relation: relationId
        ? {
            id: relationId,
          }
        : null,
      dateFrom: moment.utc([year, 0, 1]),
      dateUntil: moment.utc([year, 11, 31]),
      period: getRegistrationPeriodFilter(year),
      finalInvoiceStreamId: invoiceStreamId,
      filters: {
        // Clear all column filters
        type: undefined,
        invoiceForfaitStatus: undefined,
        isBillable: undefined,
        invoiceStatus: undefined,
        neverInvoice: undefined,
      },
    },
    getRegistrationOverviewNavParamsFromCalcSettings(calcSettings),
  );

export const getDisplayProperties = x => {
  const amountUsed = x.budgetAmountUsed ?? getUsedBudgetPercentage(x.budgetAmount, x.actualAmount);
  const hoursUsed = x.budgetHoursUsed ?? getUsedBudgetPercentage(x.budgetHours, x.actualHours);

  return {
    pbAmountClass: getPercentageColorBgClass(amountUsed),
    pbAmountStyle: { width: getFomattedPercentage(amountUsed, 100) },
    pbHoursClass: getPercentageColorBgClass(hoursUsed),
    pbHoursStyle: { width: getFomattedPercentage(hoursUsed, 100) },
    amountClass: getPercentageColorClass(amountUsed),
    hoursClass: getPercentageColorClass(hoursUsed),
  };
};

export const mapBudgetKpi = data => {
  if (!data) return null;

  const { budgetAmountUsed, budgetHoursUsed, maxBudgetUsed, details } = data;

  const maxPercentage = Math.max(budgetAmountUsed, budgetHoursUsed, 0);

  const result = {
    ...data,
    pbClass: getPercentageColorBgClass(Math.max(maxBudgetUsed, maxPercentage)), // Color code for max % of details
    ...getDisplayProperties(data),
    pbPercentage: getFomattedPercentage(maxPercentage),
    pbStyle: { width: getFomattedPercentage(maxPercentage, 100) },

    // Map details
    details: details.map(x => ({
      ...x,
      actualAmount: x.actualAmount || 0,
      actualHours: x.actualHours || 0,
      ...getDisplayProperties(x),
    })),
  };

  return result;
};
